import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calculator,
  FileText,
  IndianRupee,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import { formatIndianNumberShort } from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const LumpsumCalculator = () => {
  // Lumpsum Calculator State
  const [investmentAmount, setInvestmentAmount] = useState<number | null>(
    100000
  );
  const [expectedReturn, setExpectedReturn] = useState<number | null>(12);
  const [timePeriod, setTimePeriod] = useState<number | null>(10);
  const [results, setResults] = useState({
    totalInvestment: 0,
    estimatedReturns: 0,
    maturityValue: 0,
  });

  // Calculate Lumpsum returns
  const calculateLumpsum = useCallback(() => {
    // Handle null values by using defaults
    const investment = investmentAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;

    const totalInvestment = investment;

    // Compound interest formula: A = P(1 + r/100)^t
    const maturityValue = investment * Math.pow(1 + returnRate / 100, period);
    const estimatedReturns = maturityValue - totalInvestment;

    setResults({
      totalInvestment,
      estimatedReturns,
      maturityValue,
    });
  }, [investmentAmount, expectedReturn, timePeriod]);

  // Generate chart data for investment vs value comparison
  const generateChartData = () => {
    // Handle null values by using defaults
    const investment = investmentAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;

    const years = [];
    const investedData = [];
    const portfolioValueData = [];

    const annualRate = returnRate / 100;

    // Add starting point (Today)
    years.push('Today');
    investedData.push(investment);
    portfolioValueData.push(investment);

    for (let year = 1; year <= period; year++) {
      const portfolioValue = investment * Math.pow(1 + annualRate, year);

      years.push(`Year ${year}`);
      investedData.push(investment);
      portfolioValueData.push(portfolioValue);
    }

    return {
      labels: years,
      datasets: [
        {
          label: 'Amount invested',
          data: investedData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}20`,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.accent,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Portfolio value',
          data: portfolioValueData,
          borderColor: COLORS.sage,
          backgroundColor: `${COLORS.sage}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: COLORS.sage,
          pointBorderColor: COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const generateTableData = () => {
    // Handle null values by using defaults
    const investment = investmentAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;

    const tableData = [];
    const annualRate = returnRate / 100;

    // Add initial row (Today/Beginning)
    tableData.push({
      year: 'Today',
      isInitial: true,
      invested: investment,
      portfolioValue: investment,
      gains: 0,
    });

    for (let year = 1; year <= period; year++) {
      const portfolioValue = investment * Math.pow(1 + annualRate, year);
      const gains = portfolioValue - investment;

      tableData.push({
        year,
        isInitial: false,
        invested: investment,
        portfolioValue,
        gains,
      });
    }

    return tableData;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.smoke,
        },
      },
      y: {
        grid: {
          color: `${COLORS.smoke}20`,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.charcoal,
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    calculateLumpsum();
  }, [calculateLumpsum]);

  return (
    <ToolLayout
      title={`Lumpsum calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="Lumpsum calculator"
      description="Calculate returns on your one-time investment in any asset class"
      canonicalUrl="https://niveshright.com/tools/lumpsum-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Calculator size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="lumpsum calculator, one-time investment, mutual fund calculator, lumpsum returns, investment calculator, compound interest"
      cta={
        <WhatsAppCTA
          title="Ready to make a lumpsum investment?"
          description="Get expert advice on the best mutual funds for your lumpsum investment."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your Lumpsum calculator with these details:

Investment amount: ₹${(investmentAmount || 0).toLocaleString('en-IN')}
Investment period: ${timePeriod || 0} years
Expected return: ${expectedReturn || 0}% p.a.

CALCULATION RESULTS:
• Maturity value: ₹${results.maturityValue.toLocaleString('en-IN')}
• Estimated returns: ₹${results.estimatedReturns.toLocaleString('en-IN')}
• Growth percentage: ${((results.maturityValue / results.totalInvestment - 1) * 100).toFixed(1)}%
• Return multiple: ${(results.maturityValue / results.totalInvestment).toFixed(2)}x

I'd like personalized advice to invest this lumpsum amount. Can you help me choose the best mutual funds and investment strategy?`}
          buttonText="Get investment advice"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Investment details
          </h2>

          {/* Investment Amount */}
          <NumberInputWithSlider
            label="One-time investment amount"
            value={investmentAmount}
            onChange={setInvestmentAmount}
            min={1000}
            max={10000000000}
            step={1000}
            placeholder="100000"
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(1000, false)}`}
            maxLabel={`₹${formatIndianNumberShort(10000000000, false)}`}
            className="mb-8"
          />

          {/* Expected Return */}
          <NumberInputWithSlider
            label="Expected annual return (%)"
            value={expectedReturn}
            onChange={setExpectedReturn}
            min={1}
            max={100}
            step={0.5}
            placeholder="12"
            minLabel="1%"
            maxLabel="100%"
            className="mb-8"
          />

          {/* Time Period */}
          <NumberInputWithSlider
            label="Investment period (years)"
            value={timePeriod}
            onChange={setTimePeriod}
            min={1}
            max={85}
            step={1}
            placeholder="10"
            minLabel="1 year"
            maxLabel="85 years"
            className="mb-8"
          />
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Investment amount"
              value={<LocalizedCurrency value={results.totalInvestment} />}
              subtitle="One-time investment"
              icon={IndianRupee}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.6}
            />

            <ResultSummaryCard
              title="Estimated returns"
              value={<LocalizedCurrency value={results.estimatedReturns} />}
              subtitle="Wealth gained from investment"
              icon={TrendingUp}
              iconColor={COLORS.sage}
              valueColor={COLORS.sage}
              delay={0.7}
            />

            <ResultSummaryCard
              title="Maturity value"
              value={<LocalizedCurrency value={results.maturityValue} />}
              subtitle={`Total corpus after ${timePeriod} years`}
              icon={Target}
              iconColor={COLORS.gold}
              valueColor={COLORS.gold}
              delay={0.8}
            />
          </div>

          {/* Growth Chart */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <BarChart3
                size={24}
                style={{ color: COLORS.gold }}
                className="mr-3"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Investment growth over time
              </h3>
            </div>

            <div className="h-80">
              <Line data={generateChartData()} options={chartOptions} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">
                  Total invested
                </p>
                <p
                  className="text-lg font-bold"
                  style={{ color: COLORS.accent }}
                >
                  <LocalizedCurrency value={results.totalInvestment} />
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">Final value</p>
                <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                  <LocalizedCurrency value={results.maturityValue} />
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl text-center">
              <p className="text-sm font-medium text-gray-600 mb-1">
                Wealth gained
              </p>
              <p className="text-2xl font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.estimatedReturns} />
              </p>
              <p className="text-sm text-gray-500">
                {(
                  (results.maturityValue / results.totalInvestment - 1) *
                  100
                ).toFixed(1)}
                % growth •{' '}
                {(results.maturityValue / results.totalInvestment).toFixed(2)}x
                multiplier
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <div className="flex items-center mb-6">
          <FileText size={24} style={{ color: COLORS.gold }} className="mr-3" />
          <h3
            className="text-lg font-semibold"
            style={{ color: COLORS.primary }}
          >
            Year-wise investment breakdown
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th
                  className="text-left py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Period
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Amount invested
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Portfolio value
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Gains
                </th>
              </tr>
            </thead>
            <tbody>
              {generateTableData().map((row, index) => (
                <tr
                  key={`${row.year}-${index}`}
                  className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors duration-200 ${row.isInitial ? 'bg-blue-50 font-medium' : ''}`}
                >
                  <td
                    className="py-3 px-2 font-medium"
                    style={{ color: COLORS.primary }}
                  >
                    {row.isInitial ? 'Today (start)' : `Year ${row.year}`}
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.accent }}
                  >
                    {row.isInitial ? (
                      <LocalizedCurrency value={row.invested} />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.sage }}
                  >
                    <LocalizedCurrency value={row.portfolioValue} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: row.gains >= 0 ? COLORS.sage : '#ef4444' }}
                  >
                    <LocalizedCurrency value={row.gains} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Final investment
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.accent }}>
                <LocalizedCurrency value={results.totalInvestment} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Final portfolio value
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.maturityValue} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total wealth gained
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.estimatedReturns} />
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200 text-xs">
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <ul className="space-y-3">
                <li>
                  <strong>Period:</strong> Timeline of your investment journey
                </li>
                <li>
                  <strong>Amount invested:</strong> Your one-time lumpsum
                  investment (only at start)
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-3">
                <li>
                  <strong>Portfolio value:</strong> How your investment grows
                  over time
                </li>
                <li>
                  <strong>Gains:</strong> Extra wealth created through compound
                  growth
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="mt-16 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1 }}
      >
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            What is lumpsum investment?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>Lumpsum investment</strong> means investing a large amount
            of money all at once, rather than spreading it over time. It's like
            buying a house with cash instead of taking a loan - you pay the full
            amount upfront and own it completely from day one.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            An example
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Imagine you receive ₹5 lakh as a bonus. Instead of keeping it in a
            savings account earning 3-4% interest, you invest the entire amount
            in mutual funds. This one-time investment is called a lumpsum
            investment.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            When to choose lumpsum
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Lumpsum works best when you have a large amount available (bonus,
            inheritance, maturity proceeds) and markets are at reasonable
            levels. The entire amount gets the full investment period to grow
            through compounding.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Important terms
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Compound interest:</strong> Your money earns returns, and
              those returns also earn returns
            </p>
            <p className="text-lg text-gray-700">
              <strong>Market timing:</strong> Investing when markets are at
              lower levels for better returns
            </p>
            <p className="text-lg text-gray-700">
              <strong>Portfolio:</strong> Your investment collection - all your
              assets
            </p>
            <p className="text-lg text-gray-700">
              <strong>Maturity value:</strong> Total amount you'll receive after
              the investment period
            </p>
            <p className="text-lg text-gray-700">
              <strong>Returns:</strong> Profit earned on your investment - much
              higher than bank interest
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Lumpsum in equity mutual funds vs lumpsum in traditional assets
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Lumpsum investment type
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Expected returns
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Characteristics
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Bank savings
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">3-4%</td>
                  <td className="py-3 px-4 text-gray-700">
                    Safe but inflation eats returns
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Fixed deposit (FD)
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">6-7%</td>
                  <td className="py-3 px-4 text-gray-700">
                    Safe but limited growth
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Equity mutual funds
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    12-15%*
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Higher growth potential
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-sm text-gray-500 mt-2 italic">
              *Historical average, not guaranteed
            </p>
          </div>
        </article>
      </motion.div>
    </ToolLayout>
  );
};
